import { useCallback, useMemo, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import { Inbox } from "lucide-react";
import TemplateSelect from "./components/TemplateSelect";
import EventCard from "./components/EventCard";
import useApi from "../../../../../utils/api";
import { weekdays } from "../../../inputs/WeekdaySelect/types";

const VISIBLE_PER_DAY = 2;

function Schedule({ user }: any) {
    const { t } = useTranslation();
    const [selectedTemplate, setSelectedTemplate] = useState<{ id: string; name: string } | null>(null);
    const [expandedDays, setExpandedDays] = useState<Record<string, boolean>>({});
    const [expandedEvents, setExpandedEvents] = useState<Record<string, boolean>>({});
    const { authGet } = useApi();

    const { data: eventsData, isLoading: loadingEvents } = useQuery({
        queryKey: [
            `/shift-templates/v2/${selectedTemplate?.id}`,
            { mode: "week", page: 1, perPage: 1, userId: user?.id }
        ],
        queryFn: ({ queryKey }) => {
            const [endpoint, params] = queryKey;
            return authGet(endpoint as string, { params });
        },
        enabled: !!selectedTemplate?.id,
    });

    const grouped = useMemo(() => {
        const rawEvents = eventsData?.data?.events ?? eventsData?.events;
        const events: any[] = rawEvents ?? [];
        const groups: Record<string, any[]> = {};
        for (const event of events) {
            const day = event.date?.toLowerCase?.() ?? "monday";
            if (!groups[day]) groups[day] = [];
            groups[day].push(event);
        }
        return groups;
    }, [eventsData]);

    const orderedDays = selectedTemplate ? weekdays : [];

    const toggleDay = useCallback((day: string) => {
        setExpandedDays((prev) => ({ ...prev, [day]: !prev?.[day] }));
    }, []);

    const toggleEvent = useCallback((id: string) => {
        setExpandedEvents((prev) => ({ ...prev, [id]: !prev?.[id] }));
    }, []);


    return (
        <div className="tw-flex tw-flex-col tw-gap-3">
            <TemplateSelect
                user={user}
                selectedTemplate={selectedTemplate}
                setSelectedTemplate={setSelectedTemplate}
                setExpandedDays={setExpandedDays}
                setExpandedEvents={setExpandedEvents}
            />

            {loadingEvents ? (
                <div className="tw-border tw-rounded-xl">
                    <ul className="tw-divide-y">
                        {Array.from({ length: 4 }).map((_, i) => (
                            <li key={i} className="tw-px-4 tw-py-4 tw-space-y-2">
                                <div className="tw-h-3 tw-w-20 tw-rounded tw-bg-muted tw-animate-pulse" />
                                <div className="tw-h-14 tw-rounded-lg tw-bg-muted/60 tw-animate-pulse" />
                            </li>
                        ))}
                    </ul>
                </div>
            ) : orderedDays.length === 0 ? (
                <div className="tw-flex tw-flex-col tw-text-neutral-500 tw-justify-center tw-items-center tw-py-8 tw-border tw-border-dashed tw-rounded-lg">
                    <Inbox />
                    <p className="tw-text-muted-foreground">{t("no-data")}</p>
                </div>
            ) : (
                <div className="tw-border tw-rounded-xl">
                    <ul className="tw-divide-y">
                        {orderedDays.map((day) => {
                            const dayEvents = grouped[day] ?? [];
                            const isDayExpanded = expandedDays[day];
                            const visible = isDayExpanded ? dayEvents : dayEvents.slice(0, VISIBLE_PER_DAY);
                            const hiddenCount = dayEvents.length - VISIBLE_PER_DAY;

                            return (
                                <li key={day} className="tw-px-4 tw-py-4 tw-space-y-2">
                                    <p className="tw-text-xs tw-font-semibold tw-text-muted-foreground tw-uppercase tw-tracking-wide">
                                        {t(day)}
                                    </p>

                                    {dayEvents.length === 0 && (
                                        <p className="tw-text-xs tw-text-muted-foreground/60 tw-italic">
                                            {t("no-events")}
                                        </p>
                                    )}

                                    {visible.map((event: any) => (
                                        <EventCard
                                            key={event.id}
                                            event={event}
                                            isOpen={expandedEvents[event.id]}
                                            onToggle={toggleEvent}
                                        />
                                    ))}

                                    {!isDayExpanded && hiddenCount > 0 && (
                                        <button
                                            type="button"
                                            onClick={() => toggleDay(day)}
                                            className="tw-text-xs tw-text-primary hover:tw-underline"
                                        >
                                            +{hiddenCount} {t("more")}
                                        </button>
                                    )}
                                    {isDayExpanded && dayEvents.length > VISIBLE_PER_DAY && (
                                        <button
                                            type="button"
                                            onClick={() => toggleDay(day)}
                                            className="tw-text-xs tw-text-primary hover:tw-underline"
                                        >
                                            {t("show-less")}
                                        </button>
                                    )}
                                </li>
                            );
                        })}
                    </ul>
                </div>
            )}
        </div>
    );
}

export default Schedule;
